import {enqueueSnackbar} from 'notistack';

import {
  API_BASE_URL,
  USER_CREATED_SUCCESS,
  USER_CREATED_ERROR,
  USER_EDITED_SUCCESS,
  USER_EDITED_ERROR,
  USER_UNLOCKED_SUCCESS,
  USER_DELETED_SUCCESS,
  USER_DELETED_ERROR,
  PASSWORD_RESET_MAIL_SENT,
} from '../../constants';
import {
  UserCreateRequestDto,
  UserEditRequestDto,
  UserPasswordChangeRequestDto,
  UserResponseDto,
  UserSearchRequestDto,
} from '../../models';
import {get, post, del, put, head} from '../api';

export function createUser(
  token: string,
  data: UserCreateRequestDto,
  successActions: () => void,
) {
  post(`${API_BASE_URL}/user/create`, {
    data: data,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then(_res => {
      enqueueSnackbar(USER_CREATED_SUCCESS, {variant: 'success'});
      successActions();
    })
    .catch(err => {
      enqueueSnackbar(USER_CREATED_ERROR, {variant: 'error'});
      switch (err?.response?.status) {
        case 400: {
          const errors = new Map(Object.entries(err.response.data));
          errors.forEach((value, key) => {
            enqueueSnackbar(`Feld ${key}: ${value}`, {variant: 'warning'});
          });
          break;
        }
      }
    });
}

export function editUserData(
  token: string,
  data: UserEditRequestDto,
  handleResponse: (user: UserResponseDto) => void,
) {
  const userId = data.id;
  post(`${API_BASE_URL}/user/edit/${userId}`, {
    data: data,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      enqueueSnackbar(USER_EDITED_SUCCESS, {variant: 'success'});
      handleResponse(res.data);
    })
    .catch(err => {
      enqueueSnackbar(USER_EDITED_ERROR, {variant: 'error'});
      switch (err?.response?.status) {
        case 400: {
          const errors = new Map(Object.entries(err.response.data));
          errors.forEach((value, key) => {
            enqueueSnackbar(`Feld ${key}: ${value}`, {variant: 'warning'});
          });
          break;
        }
      }
    });
}

export function setUserLock(
  token: string,
  id: string,
  value: boolean,
  handleResponse: (user: UserResponseDto) => void,
) {
  get(`${API_BASE_URL}/user/lock/${id}/${value}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      const user: UserResponseDto = res.data;
      const action = user.locked ? 'gesperrt' : 'entsperrt';
      enqueueSnackbar(USER_UNLOCKED_SUCCESS.replace('{}', action), {
        variant: 'success',
      });
      handleResponse(user);
    })
    .catch(_err => {
      enqueueSnackbar(USER_EDITED_ERROR, {variant: 'error'});
    });
}

export function deleteUser(
  token: string,
  userId: string,
  updateTable: (userId: string) => void,
) {
  del(`${API_BASE_URL}/user/delete/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then(_res => {
      enqueueSnackbar(USER_DELETED_SUCCESS, {variant: 'success'});
      updateTable(userId);
    })
    .catch(_err => {
      enqueueSnackbar(USER_DELETED_ERROR, {variant: 'error'});
    });
}
export function sendPasswordResetMail(
  token: string,
  id: string,
  endActions: () => void,
) {
  put(`${API_BASE_URL}/user/send_password_mail/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then(_res => {
      enqueueSnackbar(PASSWORD_RESET_MAIL_SENT, {variant: 'success'});
    })
    .catch(err => {
      enqueueSnackbar(err.response?.data, {variant: 'error'});
    })
    .finally(() => {
      endActions();
    });
}

export function searchUser(
  usergroups: string[],
  text: string,
  token: string | undefined,
  signal?: AbortSignal,
  pageSize: number = 10,
  page: number = 0,
  sort: string = '',
) {
  return post(
    `${API_BASE_URL}/user/search?size=${pageSize}&page=${page}${sort}`,
    {
      data: JSON.stringify({
        usergroups,
        text,
      } satisfies UserSearchRequestDto),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      responseType: 'json',
      signal,
    },
  );
}

export function validatePasswordReset(requestId: string) {
  return head(`${API_BASE_URL}/user/resetPassword/${requestId}`, {
    headers: {'Content-Type': 'application/json'},
  });
}

export function resetPassword(requestId: string | undefined, data: string) {
  return post(`${API_BASE_URL}/user/resetPassword/${requestId}`, {
    data: data,
    headers: {'Content-Type': 'application/json'},
  });
}

export function changePassword(
  valueMap: Map<string, string>,
  token: string | undefined,
) {
  const data: UserPasswordChangeRequestDto = Object.fromEntries(
    valueMap,
  ) as unknown as UserPasswordChangeRequestDto;
  return post(`${API_BASE_URL}/user/changePassword`, {
    data: JSON.stringify(data),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}
